import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Handle,
  Position,
  NodeProps,
  useReactFlow,
  useUpdateNodeInternals,
  useNodes,
} from "reactflow";
import {
  Signpost,
  FlaskConical,
  Plus,
  Trash2,
  ChevronRight,
  Hash,
  Type,
  ToggleLeft,
  Calendar,
  Braces,
  List,
  Clock,
  Workflow,
} from "lucide-react";
import { Input } from "@components/ui/input";
import { Button } from "@components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@components/ui/dropdown-menu";
import Confirmation from "@components/common/ConfirmDialog";
import { toast } from "@components/ui/use-toast";
import { useQuery } from "react-query";
import { getConnectedInputs, getExecutionPath } from "../common/nodeDataFlow";
import PaginatedInputViewer from "../common/PaginatedInputViewer";
import ResponseViewer from "../common/ResponseViewer";
import NodeContentSection from "../common/NodeContentSection";
import { executeWorkflow, getConditions } from "../createPluginApis";
import { handlerStyle } from "../common/commonData";
import {
  getNodeExecutionParameters,
  buildWorkflowPayload,
  handleNodeExecutionResults,
  findDownstreamNodes,
} from "../utils/nodeExecutionUtils";
import { getNodeBackendType } from "../utils/nodeTransformUtils";
import CollapsedNodeView from "../common/NodeHeaderView";
import NodeContextMenu, {
  useNodeContextMenu,
  createContextMenuItems,
} from "../common/NodeContextMenu";
import { FullscreenWrapper } from "../common/withFullscreen";
import { FullscreenToggleButton } from "../common/FullscreenToggleButton";
import { ValidatedTitleInput } from "../components/ValidatedTitleInput";
import ExpressionEditor from "../common/ExpressionEditor";
import { flattenObject } from "../utils/fieldHelpers";
import { useResizablePanels } from "../hooks/useResizablePanels";
import { PanelDivider } from "../common/PanelDivider";

interface Condition {
  id: string;
  leftValue: string;
  rightValue: any;
  operator: {
    type: string;
    operation: string;
  };
}

interface Rule {
  id: string;
  conditions: {
    options: {
      caseSensitive: boolean;
      leftValue: string;
      typeValidation: string;
    };
    conditions: Condition[];
    combinator: string;
  };
  renameOutput: boolean;
  outputKey: string;
}

interface SwitchNodeData {
  label?: string;
  uid: string;
  title: string;
  description?: string;
  mode?: string;
  rules?: {
    values: Rule[];
  };
  looseTypeValidation?: boolean;
  options?: Record<string, any>;
  onDelete?: (id: string) => void;
  onDataChange?: (id: string, updates: any) => void;
  borderColor?: string;
  shadowColor?: string;
}

// Helper to ensure all rules have IDs (moved outside component to avoid hook issues)
const ensureRuleIds = (rules: Rule[]): Rule[] => {
  return rules.map((rule) => {
    if (!rule.id) {
      // Generate ID if missing
      return {
        ...rule,
        id: `rule_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        conditions: {
          ...rule.conditions,
          conditions: rule.conditions.conditions.map((cond) => {
            if (!cond.id) {
              return {
                ...cond,
                id: `condition_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
              };
            }
            return cond;
          }),
        },
      };
    }
    // Ensure conditions also have IDs
    return {
      ...rule,
      conditions: {
        ...rule.conditions,
        conditions: rule.conditions.conditions.map((cond) => {
          if (!cond.id) {
            return {
              ...cond,
              id: `condition_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
            };
          }
          return cond;
        }),
      },
    };
  });
};

const SwitchNode: React.FC<NodeProps<SwitchNodeData>> = ({ id, data }) => {
  const label = data.label || "SWITCH";
  const [uid, setUid] = useState(data.uid || "");
  const [title, setTitle] = useState(data.title || "");
  const [description, setDescription] = useState(data.description || "");
  const [isExpanded, setIsExpanded] = useState(false);

  // Hook to update handle positions in React Flow when they change
  const updateNodeInternals = useUpdateNodeInternals();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isTitleValid, setIsTitleValid] = useState(true);

  const nodes = useNodes();

  // Context menu
  const {
    showContextMenu,
    contextMenuPosition,
    handleContextMenu,
    closeContextMenu,
  } = useNodeContextMenu(id);

  const [rules, setRules] = useState<Rule[]>(() => {
    const loadedRules =
      (data as any).storedParameters?.rules?.values || data.rules?.values || [];
    return ensureRuleIds(loadedRules);
  });

  // Calculate dynamic height for collapsed node based on number of rules
  const collapsedHeight = useMemo(() => {
    const baseHeight = 112; // h-28 = 7rem = 112px
    const additionalHeight = rules.length > 2 ? (rules.length - 2) * 32 : 0;
    return baseHeight + additionalHeight;
  }, [rules.length]);

  // Input/Output state
  const [inputData, setInputData] = useState<any[]>([]);
  const [outputData, setOutputData] = useState<any>(null);
  const [errorResponse, setErrorResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [ruleNameErrors, setRuleNameErrors] = useState<Map<string, string>>(
    new Map(),
  );

  const reactFlowInstance = useReactFlow();
  const { getNodes, getEdges } = useReactFlow();
  const { containerRef, widths, startDrag } = useResizablePanels([
    33.33, 33.33, 33.34,
  ]);

  // Fetch condition operators
  const {
    data: operatorsData,
    isLoading: operatorsLoading,
    error: operatorsError,
  } = useQuery("conditionOperators", getConditions, {
    staleTime: Infinity, // Cache the operators data indefinitely
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    onError: (error: any) => {
      console.error("Failed to fetch condition operators:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load condition operators",
        duration: 3000,
      });
    },
  });

  // Auto-generate UID if not present
  useEffect(() => {
    if (!uid) {
      const newUid = `switch_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      setUid(newUid);
    }
  }, []);

  // Refresh input data when node connections change
  useEffect(() => {
    refreshInputData();
    const interval = setInterval(refreshInputData, 3000);
    return () => clearInterval(interval);
  }, [id]);

  // Sync state to parent with storedParameters
  useEffect(() => {
    if (!data.onDataChange) return;

    // Compute mode and looseTypeValidation from data (prevents dependency issues)
    const currentMode =
      (data as any).storedParameters?.mode || (data.mode as any) || "rules";
    const currentLooseTypeValidation =
      (data as any).storedParameters?.looseTypeValidation ??
      data.looseTypeValidation ??
      true;

    // Build storedParameters for backend
    const storedParameters = {
      mode: currentMode,
      rules: {
        values: rules,
      },
      looseTypeValidation: currentLooseTypeValidation,
      options: data.options || {},
    };

    const updateData: any = {
      uid,
      title,
      description,
      mode: currentMode,
      rules: { values: rules },
      looseTypeValidation: currentLooseTypeValidation,
      options: data.options || {},
      outputData,
      response: outputData,
      sub_type: "switch_case",
      node_type: "flow",
      storedParameters,
    };

    // CRITICAL: Always sync node_data with current rules
    // This ensures handles are properly registered in React Flow
    const existingNodeData = (data as any).node_data || [];

    // Always regenerate node_data to match current rules (handles additions/deletions)
    if (rules.length > 0) {
      updateData.node_data = rules.map((rule, index) => {
        // Preserve itemCount from existing node_data if available
        const existingData = existingNodeData[index];
        return {
          id: `output_${index}`,
          outputKey: rule.outputKey || `Rule ${index + 1}`,
          itemCount: existingData?.itemCount || 0,
        };
      });
    } else {
      // No rules - clear node_data
      updateData.node_data = [];
    }

    data.onDataChange(id, updateData);
  }, [uid, title, description, rules, outputData, data, id]);

  // Ref to track previous outputData for sync effect
  const prevOutputDataRef = useRef<any>(null);

  // Sync outputData from node data (updated during workflow execution)
  // Use deep comparison to prevent unnecessary updates
  useEffect(() => {
    const newOutputData = (data as any).outputData || (data as any).response;

    // Skip if no new data
    if (!newOutputData) {
      // Clear output if explicitly set to null/undefined
      if (outputData !== null && newOutputData === null) {
        prevOutputDataRef.current = null;
        setOutputData(null);
      }
      return;
    }

    // Skip if it's the same reference (optimization)
    if (prevOutputDataRef.current === newOutputData) {
      return;
    }

    // Deep comparison - only update if stringified content changed
    const newDataStr = JSON.stringify(newOutputData);
    const prevDataStr = prevOutputDataRef.current
      ? JSON.stringify(prevOutputDataRef.current)
      : null;

    if (newDataStr !== prevDataStr) {
      prevOutputDataRef.current = newOutputData;

      let transformedData = newOutputData;
      let generatedNodeData: any[] = [];

      // Check if data is in raw backend format (nested arrays without 'rule' property)
      if (
        Array.isArray(newOutputData) &&
        newOutputData.length > 0 &&
        Array.isArray(newOutputData[0]) &&
        !(typeof newOutputData[0] === "object" && "rule" in newOutputData[0])
      ) {
        // Transform raw nested arrays to switch format
        transformedData = newOutputData.map(
          (outputArray: any[], index: number) => {
            // Unwrap json_data from each item
            const unwrappedItems = Array.isArray(outputArray)
              ? outputArray.map((item: any) => {
                  if (item && typeof item === "object" && "json_data" in item) {
                    return item.json_data;
                  }
                  return item;
                })
              : [];

            return {
              rule: rules[index]?.outputKey || `Rule ${index + 1}`,
              ruleIndex: index,
              items: unwrappedItems,
              itemCount: unwrappedItems.length,
            };
          },
        );

        // Generate node_data for handles when transforming raw format
        generatedNodeData = newOutputData.map(
          (outputArray: any[], index: number) => ({
            id: `output_${index}`,
            outputKey: rules[index]?.outputKey || `Output ${index + 1}`,
            itemCount: Array.isArray(outputArray) ? outputArray.length : 0,
          }),
        );
      } else if (
        Array.isArray(transformedData) &&
        transformedData.length > 0 &&
        transformedData[0] !== null &&
        typeof transformedData[0] === "object" &&
        "rule" in transformedData[0]
      ) {
        // Data is already in switch format - generate node_data from it
        generatedNodeData = transformedData.map(
          (ruleOutput: any, index: number) => ({
            id: `output_${index}`,
            outputKey: ruleOutput.rule || `Rule ${index + 1}`,
            itemCount: ruleOutput.itemCount || 0,
          }),
        );
      }

      setOutputData(transformedData);

      // Update parent with generated node_data if we created it
      // AND update response/outputData with transformed data
      if (data.onDataChange) {
        const updates: any = {};

        if (generatedNodeData.length > 0) {
          updates.node_data = generatedNodeData;
        }

        // CRITICAL: Always update response and outputData with transformed data
        // This ensures downstream nodes receive the correct switch format
        updates.response = transformedData;
        updates.outputData = transformedData;

        data.onDataChange(id, updates);
      }
    } else {
      // Content is the same, just update the reference to prevent future comparisons
      prevOutputDataRef.current = newOutputData;
    }
  }, [(data as any).outputData, (data as any).response, rules]);

  // Sync error from node data (set during global workflow execution)
  useEffect(() => {
    const executionStatus = (data as any).executionStatus;
    const error = (data as any).error;

    // If node failed during global execution, show error in ResponseViewer
    if (executionStatus === "failed" && error) {
      setErrorResponse(error);
    } else if (executionStatus === "success") {
      // Clear error when node succeeds
      setErrorResponse(null);
    }
  }, [(data as any).executionStatus, (data as any).error]);

  // Update React Flow handle positions when rules, node_data, or expansion state changes
  // This ensures edge drawing works correctly in all states
  useEffect(() => {
    // Delay the update to ensure DOM has been updated
    const timer = setTimeout(() => {
      updateNodeInternals(id);
    }, 0);

    return () => clearTimeout(timer);
  }, [
    rules.length,
    (data as any).node_data,
    isExpanded,
    id,
    updateNodeInternals,
  ]);

  const refreshInputData = () => {
    const nodes = getNodes();
    const edges = getEdges();
    const connectedInputs = getConnectedInputs(id, edges, nodes);

    setInputData((prevData) => {
      if (JSON.stringify(prevData) === JSON.stringify(connectedInputs)) {
        return prevData;
      }
      return connectedInputs;
    });
  };

  // Get display data for InputViewer
  const getDisplayData = (input: any) => {
    // Helper to unwrap json_data from backend format
    const extractJsonData = (data: any): any => {
      if (!data) return data;

      if (Array.isArray(data)) {
        return data.map((item: any) => {
          if (item && typeof item === "object" && "json_data" in item) {
            return item.json_data;
          }
          return item;
        });
      }

      if (data && typeof data === "object" && "json_data" in data) {
        return data.json_data;
      }

      return data;
    };

    // Priority: response > outputData > configData
    if (input.response) {
      let data = extractJsonData(input.response);

      // Unwrap single-element wrapper arrays
      if (Array.isArray(data) && data.length === 1) {
        data = data[0];
      }

      return data;
    }

    if (input.outputData) {
      let data = extractJsonData(input.outputData);

      // Unwrap single-element wrapper arrays
      // [{users: [...], total: 208}] → {users: [...], total: 208}
      // But keep multi-element arrays: [{...}, {...}] → [{...}, {...}]
      if (Array.isArray(data) && data.length === 1) {
        data = data[0];
      }

      return data;
    }

    // For configData, create an object
    const displayData: any = {};
    const EXCLUDE = [
      "uid",
      "title",
      "description",
      "label",
      "position",
      "nodeType",
      "isDecript",
      "isDecrypted",
      "testConnectionBtn",
      "tabId",
      "configType",
      "onDelete",
      "onDataChange",
      "header",
      "borderColor",
      "shadowColor",
    ];
    if (input.configData) {
      // Get list of encrypted fields from isDecript array
      const encryptedFields = input.configData?.isDecript || [];

      Object.entries(input.configData)
        .filter(([key]) => !EXCLUDE.includes(key))
        .forEach(([key, value]) => {
          // Mask encrypted fields with asterisks
          if (Array.isArray(encryptedFields) && encryptedFields.includes(key)) {
            displayData[key] = "********************";
          } else {
            displayData[key] = value;
          }
        });
    }

    return displayData;
  };

  // Prepare available fields for ExpressionEditor autocomplete
  const getAvailableFields = React.useMemo((): Array<{
    path: string;
    value: any;
  }> => {
    const fields: Array<{ path: string; value: any }> = [];

    if (inputData && inputData.length > 0) {
      const firstInput = inputData[0];
      const displayData = getDisplayData(firstInput);
      const prefix = firstInput.configData ? "config" : "$json";

      // Add root $json entry for ExpressionEditor evaluation
      if (
        prefix === "$json" &&
        displayData &&
        Object.keys(displayData).length > 0
      ) {
        fields.push({ path: "$json", value: displayData });
      }

      flattenObject(displayData, prefix, fields);
    }

    return fields;
  }, [JSON.stringify(inputData)]);

  const handleDelete = (nodeId: string) => {
    setDeleteId(nodeId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (deleteId && data.onDelete) {
      data.onDelete(deleteId);
    }
    setShowDeleteConfirm(false);
    setDeleteId(null);
  };

  // Add a new rule
  const addRule = () => {
    const newRule: Rule = {
      id: `rule_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      conditions: {
        options: {
          caseSensitive: true,
          leftValue: "",
          typeValidation: "loose",
        },
        conditions: [
          {
            id: `condition_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
            leftValue: "",
            rightValue: "",
            operator: {
              type: "",
              operation: "",
            },
          },
        ],
        combinator: "and",
      },
      renameOutput: false,
      outputKey: `Rule ${rules.length + 1}`,
    };

    setRules((prev) => [...prev, newRule]);

    // Update React Flow handle positions after adding rule
    setTimeout(() => updateNodeInternals(id), 0);
  };

  // Remove a rule
  const removeRule = (ruleId: string) => {
    setRules((prev) => prev.filter((r) => r.id !== ruleId));

    // Update React Flow handle positions after removing rule
    setTimeout(() => updateNodeInternals(id), 0);
  };

  // Validate rule name
  const validateRuleName = (ruleId: string, name: string): string | null => {
    // Check if empty
    if (!name || !name.trim()) {
      return "Rule name is required";
    }

    // Check length
    if (name.length > 35) {
      return "Rule name cannot exceed 35 characters";
    }

    // Check for duplicates
    const isDuplicate = rules.some(
      (r) =>
        r.id !== ruleId &&
        r.outputKey.trim().toLowerCase() === name.trim().toLowerCase(),
    );

    if (isDuplicate) {
      return "Rule name already exists. Use a unique name";
    }

    return null;
  };

  // Update rule field
  const updateRule = (ruleId: string, field: keyof Rule, value: any) => {
    // Special validation for outputKey (rule name)
    if (field === "outputKey") {
      const error = validateRuleName(ruleId, value);
      setRuleNameErrors((prev) => {
        const newErrors = new Map(prev);
        if (error) {
          newErrors.set(ruleId, error);
        } else {
          newErrors.delete(ruleId);
        }
        return newErrors;
      });
    }

    setRules((prev) =>
      prev.map((r) => (r.id === ruleId ? { ...r, [field]: value } : r)),
    );
  };

  // Add a new condition to a rule
  const addCondition = (ruleId: string) => {
    const newCondition: Condition = {
      id: `condition_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      leftValue: "",
      rightValue: "",
      operator: {
        type: "",
        operation: "",
      },
    };

    setRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId
          ? {
              ...rule,
              conditions: {
                ...rule.conditions,
                conditions: [...rule.conditions.conditions, newCondition],
              },
            }
          : rule,
      ),
    );
  };

  // Remove a condition from a rule
  const removeCondition = (ruleId: string, conditionId: string) => {
    setRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId
          ? {
              ...rule,
              conditions: {
                ...rule.conditions,
                conditions: rule.conditions.conditions.filter(
                  (c) => c.id !== conditionId,
                ),
              },
            }
          : rule,
      ),
    );
  };

  // Update condition field in a rule
  const updateCondition = (
    ruleId: string,
    conditionId: string,
    field: keyof Condition,
    value: any,
  ) => {
    setRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId
          ? {
              ...rule,
              conditions: {
                ...rule.conditions,
                conditions: rule.conditions.conditions.map((c) => {
                  if (c.id !== conditionId) return c;

                  if (field === "leftValue") {
                    if (!value || value.trim() === "") {
                      return { ...c, [field]: value, rightValue: "" };
                    }

                    const values: string[] = [];
                    const fieldValues: any[] = [];

                    // Handle $json expressions
                    const jsonMatches =
                      value.match(/\{\{\s*\$json\.?([^}]+)\}\}/g) || [];
                    jsonMatches.forEach((match: string) => {
                      const fieldPath = match
                        .replace(/\{\{\s*\$json\.?/g, "")
                        .replace(/\s*\}\}/g, "")
                        .trim();

                      let foundValue = false;
                      for (const input of inputData) {
                        const displayData = getDisplayData(input);
                        if (!displayData) continue;

                        let fieldValue: any;
                        if (fieldPath in displayData) {
                          fieldValue = displayData[fieldPath];
                        } else {
                          try {
                            fieldValue = fieldPath
                              .split(".")
                              .reduce((obj: any, key: string) => {
                                if (!obj) return undefined;

                                // Handle array access like "todo[0]" or just "[0]"
                                const arrayMatch =
                                  key.match(/^([^[]*)\[(\d+)\]$/);
                                if (arrayMatch) {
                                  const propName = arrayMatch[1]; // "todo" or empty string
                                  const index = parseInt(arrayMatch[2]); // 0

                                  if (propName) {
                                    // Handle "todo[0]" - access obj.todo[0]
                                    return obj[propName]?.[index];
                                  } else {
                                    // Handle "[0]" - access obj[0]
                                    return obj[index];
                                  }
                                }

                                // Regular property access
                                return obj[key];
                              }, displayData);
                          } catch {
                            fieldValue = undefined;
                          }
                        }

                        if (fieldValue !== undefined && fieldValue !== null) {
                          values.push(String(fieldValue));
                          fieldValues.push(fieldValue);
                          foundValue = true;
                          break;
                        }
                      }

                      if (!foundValue) {
                        const matched = getAvailableFields.find(
                          (f) =>
                            f.path === `$json.${fieldPath}` ||
                            f.path.endsWith(fieldPath),
                        );
                        if (matched?.value !== undefined) {
                          values.push(String(matched.value));
                          fieldValues.push(matched.value);
                        }
                      }
                    });

                    // Handle $("NodeName") expressions
                    const nodeMatches =
                      value.match(/\{\{\s*\$\("([^"]+)"\)\.?([^}]*)\}\}/g) ||
                      [];
                    nodeMatches.forEach((match: string) => {
                      const inner = match
                        .replace(/\{\{\s*/, "")
                        .replace(/\s*\}\}/, "")
                        .trim();
                      const nodeRefMatch = inner.match(
                        /^\$\("([^"]+)"\)\.?(.*)$/,
                      );
                      if (!nodeRefMatch) return;

                      const [, nodeName, rawPath] = nodeRefMatch;
                      const targetNode = nodes.find(
                        (n: any) =>
                          (n.data?.title || n.data?.header || "") ===
                            nodeName && n.id !== id,
                      );
                      if (!targetNode) return;

                      let rootData =
                        (targetNode as any).data?.outputData ||
                        (targetNode as any).data?.response ||
                        null;
                      if (!rootData) return;

                      if (Array.isArray(rootData) && rootData.length === 1) {
                        rootData = rootData[0];
                      }

                      let actualPath = rawPath;
                      if (actualPath === "json" || actualPath === "") return;
                      else if (actualPath.startsWith("json."))
                        actualPath = actualPath.substring(5);
                      else if (actualPath.startsWith("json["))
                        actualPath = actualPath.substring(4);

                      try {
                        const resolvedValue = actualPath
                          ? actualPath
                              .split(".")
                              .reduce((obj: any, key: string) => {
                                if (!obj) return undefined;

                                // Handle array access like "todo[0]" or just "[0]"
                                const arrayMatch =
                                  key.match(/^([^[]*)\[(\d+)\]$/);
                                if (arrayMatch) {
                                  const propName = arrayMatch[1]; // "todo" or empty string
                                  const index = parseInt(arrayMatch[2]); // 0

                                  if (propName) {
                                    // Handle "todo[0]" - access obj.todo[0]
                                    return obj[propName]?.[index];
                                  } else {
                                    // Handle "[0]" - access obj[0]
                                    return obj[index];
                                  }
                                }

                                // Regular property access
                                return obj[key];
                              }, rootData)
                          : rootData;

                        if (
                          resolvedValue !== undefined &&
                          resolvedValue !== null
                        ) {
                          values.push(
                            typeof resolvedValue === "object"
                              ? JSON.stringify(resolvedValue)
                              : String(resolvedValue),
                          );
                          fieldValues.push(resolvedValue);
                        }
                      } catch (e) {
                        console.warn(`Failed to resolve node path`, e);
                      }
                    });

                    const newRightValue = values.join(", ");

                    if (!c.operator.type && fieldValues.length > 0) {
                      autoSelectOperatorType(
                        ruleId,
                        conditionId,
                        fieldValues[0],
                      );
                    }

                    return { ...c, [field]: value, rightValue: newRightValue };
                  }

                  return { ...c, [field]: value };
                }),
              },
            }
          : rule,
      ),
    );
  };

  // Update operator for a condition in a rule
  const updateConditionOperator = (
    ruleId: string,
    conditionId: string,
    dataType: string,
    operationValue: string,
  ) => {
    setRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId
          ? {
              ...rule,
              conditions: {
                ...rule.conditions,
                conditions: rule.conditions.conditions.map((c) =>
                  c.id === conditionId
                    ? {
                        ...c,
                        operator: {
                          type: dataType,
                          operation: operationValue,
                        },
                      }
                    : c,
                ),
              },
            }
          : rule,
      ),
    );
  };

  // Auto-select operator type based on dragged field value type
  const autoSelectOperatorType = (
    ruleId: string,
    conditionId: string,
    fieldValue: any,
  ) => {
    if (!operatorsData?.data) return;

    let detectedType = "";

    // Detect the data type from the field value
    if (typeof fieldValue === "number") {
      detectedType = "number";
    } else if (typeof fieldValue === "string") {
      detectedType = "string";
    } else if (typeof fieldValue === "boolean") {
      detectedType = "boolean";
    } else if (Array.isArray(fieldValue)) {
      detectedType = "array";
    } else if (fieldValue === null || fieldValue === undefined) {
      detectedType = "string"; // Default fallback
    }

    // Find matching data type in operators data
    const matchingDataType = operatorsData.data.find(
      (dt: any) => dt.data_type === detectedType,
    );

    if (matchingDataType && matchingDataType.operator?.length > 0) {
      // Try to find "equals" operator as default, otherwise use first operator
      const equalsOperator = matchingDataType.operator.find(
        (op: any) => op.value === "equals",
      );
      const defaultOperator = equalsOperator || matchingDataType.operator[0];

      updateConditionOperator(
        ruleId,
        conditionId,
        matchingDataType.data_type,
        defaultOperator.value,
      );
    }
  };

  // Prepare conditions for payload - always include rightValue
  const prepareConditionsForPayload = (conditionsData: any) => {
    const preparedConditions = {
      ...conditionsData,
      conditions: conditionsData.conditions.map((condition: Condition) => {
        // Find the operator to check if it requires input
        const selectedDataType = operatorsData?.data?.find(
          (dt: any) => dt.data_type === condition.operator.type,
        );
        const selectedOperator = selectedDataType?.operator?.find(
          (op: any) => op.value === condition.operator.operation,
        );

        const requiresInput = selectedOperator?.requires_input !== false;

        // Always include rightValue - set to empty string if operator doesn't require input
        return {
          ...condition,
          rightValue: requiresInput ? condition.rightValue || "" : "",
        };
      }),
    };

    return preparedConditions;
  };

  // Execute workflow
  const executeSwitch = async () => {
    if (!inputData?.length) {
      toast({
        variant: "destructive",
        title: "No Input Data",
        description: "Connect a node to provide input data",
      });
      return;
    }

    if (!title.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Title is required for the SWITCH node",
        duration: 3000,
      });
      return;
    }

    // Validate rules and conditions
    const validation = validateRulesAndConditions();
    if (!validation.isValid) {
      toast({
        variant: "destructive",
        title: "Invalid Configuration",
        description: validation.errorMessage,
        duration: 4000,
      });
      return;
    }

    // Get plugin ID from session
    const pluginId = sessionStorage.getItem("editPluginId");

    if (!pluginId || pluginId === "temp-plugin") {
      toast({
        variant: "destructive",
        title: "Plugin Not Saved",
        description: "Please save the plugin first before testing",
        duration: 4000,
      });
      return;
    }

    // Get active mesh flow from sessionStorage
    const activeMeshFlowStr = sessionStorage.getItem("activeMeshFlow");
    if (!activeMeshFlowStr) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          "No active mesh flow. Please create or select a mesh flow first.",
        duration: 4000,
      });
      return;
    }

    setOutputData(null);
    setErrorResponse(null);
    setLoading(true);

    try {
      const activeMeshFlow = JSON.parse(activeMeshFlowStr);

      // Get plugin ID from session
      const pluginId = sessionStorage.getItem("editPluginId") || "";

      // Get all nodes and edges from React Flow
      const allNodes = reactFlowInstance.getNodes();
      const allEdges = reactFlowInstance.getEdges();

      // Get execution path: only nodes from start to current node (no downstream nodes)
      const executableNodes = getExecutionPath(id, allNodes, allEdges);

      // Mark all executable nodes as pending
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          // Check if this node is in the execution path
          const isExecutable = executableNodes.some(
            (n: any) => n.id === node.id,
          );
          if (isExecutable) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "pending",
                executionResult: undefined,
                error: undefined,
              },
            };
          }
          return node;
        }),
      );

      // Build nodes array for workflow
      const workflowNodes = executableNodes.map((node) => {
        let nodeType = "core";
        let subType = "http_request";
        let parameters;

        if (node.id === id) {
          // This is the current SwitchNode - build from local state
          // Compute mode and looseTypeValidation from data (prevents dependency issues)
          const currentMode =
            (data as any).storedParameters?.mode ||
            (data.mode as any) ||
            "rules";
          const currentLooseTypeValidation =
            (data as any).storedParameters?.looseTypeValidation ??
            data.looseTypeValidation ??
            true;

          const nodeParameters = {
            mode: currentMode,
            rules: {
              values: rules.map((rule) => ({
                conditions: prepareConditionsForPayload(rule.conditions),
                renameOutput: rule.renameOutput,
                outputKey: rule.outputKey,
              })),
            },
            looseTypeValidation: currentLooseTypeValidation,
            options: data.options || {},
          };

          nodeType = "flow";
          subType = "switch_case";
          parameters = {
            ...nodeParameters,
            label: label,
          };
        } else {
          // Other nodes - use centralized utility (checks storedParameters first)
          // Determine backend type and sub_type using centralized mapping
          const backendTypeInfo = getNodeBackendType(node.type || "custom");
          nodeType = backendTypeInfo.type;
          subType = backendTypeInfo.sub_type;

          // Special handling for ifElseNode with conditions
          if (node.type === "ifElseNode") {
            // For other IfElseNodes, also prepare conditions
            const otherConditions = node.data.conditions || {
              options: {
                caseSensitive: true,
                leftValue: "",
                typeValidation: "strict",
                version: 3,
              },
              conditions: [],
              combinator: "and",
            };
            const nodeParameters = {
              conditions: prepareConditionsForPayload(otherConditions),
              looseTypeValidation: node.data.looseTypeValidation || false,
              options: node.data.options || {},
            };
            parameters = {
              ...nodeParameters,
              label: node.data.label,
            };
          } else if (node.data.configData) {
            // Authentication/configuration nodes
            parameters = getNodeExecutionParameters(node, pluginId).parameters;
          } else if (node.data.sub_type) {
            // Nodes with explicit sub_type
            parameters = getNodeExecutionParameters(node, pluginId).parameters;
          } else {
            // All other nodes
            parameters = getNodeExecutionParameters(node, pluginId).parameters;
          }
        }

        return {
          uid: node.data.uid || node.id,
          title: node.data.title || node.data.label || "Untitled",
          type: nodeType,
          sub_type: subType,
          version: "1.0",
          position: [node.position.x, node.position.y],
          parameters,
        };
      });

      // Create mapping from React Flow node IDs to UIDs
      const nodeIdToUidMap: Record<string, string> = {};
      executableNodes.forEach((node) => {
        const nodeUid = node.data.uid || node.id;
        nodeIdToUidMap[node.id] = nodeUid;
      });

      // Create Set of node IDs in execution path for filtering
      const executionPathNodeIds = new Set(executableNodes.map((n) => n.id));

      const connections: any = {};
      allEdges
        .filter(
          (edge) =>
            executionPathNodeIds.has(edge.source) &&
            executionPathNodeIds.has(edge.target),
        )
        .forEach((edge) => {
          const sourceUid = nodeIdToUidMap[edge.source];
          const targetUid = nodeIdToUidMap[edge.target];

          if (!sourceUid || !targetUid) return;

          if (!connections[sourceUid]) {
            connections[sourceUid] = {
              main: [],
            };
          }

          // Check if source node is an if_else node
          const sourceNode = executableNodes.find((n) => n.id === edge.source);
          const isIfElseNode =
            sourceNode?.type === "ifElseNode" ||
            sourceNode?.data?.sub_type === "if_else";

          // Check if source node is a switch node
          const isSwitchNode =
            sourceNode?.type === "switchNode" ||
            sourceNode?.data?.sub_type === "switch_case";

          // Determine output index based on node type and handle
          let outputIndex = 0;

          if (isSwitchNode && edge.sourceHandle) {
            // For switch nodes: output_0 → main[0], output_1 → main[1], output_2 → main[2], etc.
            const handleMatch = edge.sourceHandle.match(/output_(\d+)/);
            if (handleMatch) {
              outputIndex = parseInt(handleMatch[1], 10);
            }
          } else if (isIfElseNode && edge.sourceHandle) {
            // For if_else nodes: true handle → main[0], false handle → main[1]
            outputIndex = edge.sourceHandle === "false" ? 1 : 0;
          }

          if (!connections[sourceUid].main[outputIndex]) {
            connections[sourceUid].main[outputIndex] = [];
          }

          connections[sourceUid].main[outputIndex].push({
            node: targetUid,
            type: "main",
            index: 0,
          });
        });

      // Build complete workflow payload using centralized utility
      const workflowPayload = buildWorkflowPayload(
        activeMeshFlow,
        workflowNodes,
        connections,
      );

      const result = await executeWorkflow(workflowPayload);

      // Update ALL executed nodes with their results (not just the clicked node)
      if (result?.results?.[0]?.node_results) {
        const nodeResults = result.results[0].node_results;

        // Use the utility function to handle node execution results
        const { hasErrors, errorMessage, errorNodeTitle } =
          handleNodeExecutionResults(reactFlowInstance, nodeResults, allEdges);

        // Extract this node's output for local state
        const resultsMap = new Map<string, any>();
        nodeResults.forEach((nodeResult: any) => {
          resultsMap.set(nodeResult.uid, nodeResult);
        });

        // Extract the raw output_data for switch node processing
        let rawOutputData = null;
        let nodeData: any[] = [];
        const currentNodeResult = resultsMap.get(uid || id);

        if (currentNodeResult) {
          // Check if this node failed
          const isFailed =
            currentNodeResult.status === "error" ||
            currentNodeResult.status === "failed" ||
            currentNodeResult.status === "failure";

          if (isFailed) {
            // Show error in ResponseViewer but don't pass to downstream nodes
            setErrorResponse(currentNodeResult.error || "Execution failed");
            setOutputData(null);
          } else {
            // Success - process output data
            rawOutputData = currentNodeResult.output_data;
            setErrorResponse(null);

            // Create node_data from output_data structure
            if (rawOutputData && Array.isArray(rawOutputData)) {
              const isNestedArray =
                rawOutputData.length > 0 &&
                rawOutputData.every((item: any) => Array.isArray(item));

              if (isNestedArray) {
                nodeData = rawOutputData.map(
                  (outputArray: any[], index: number) => ({
                    id: `output_${index}`,
                    outputKey: rules[index]?.outputKey || `Output ${index + 1}`,
                    itemCount: Array.isArray(outputArray)
                      ? outputArray.length
                      : 0,
                  }),
                );
              } else {
                nodeData = [
                  {
                    id: "output_0",
                    outputKey: rules[0]?.outputKey || "Output 1",
                    itemCount: rawOutputData.length,
                  },
                ];
              }
            }

            let responseData: any = null;

            if (
              rawOutputData &&
              Array.isArray(rawOutputData) &&
              rawOutputData.length > 0
            ) {
              const isNestedArray = rawOutputData.every((item: any) =>
                Array.isArray(item),
              );

              if (isNestedArray) {
                responseData = rawOutputData.map(
                  (outputArray: any[], index: number) => {
                    const unwrappedItems = outputArray.map((item: any) => {
                      if (
                        item &&
                        typeof item === "object" &&
                        "json_data" in item
                      ) {
                        return item.json_data;
                      }
                      return item;
                    });

                    return {
                      rule: rules[index]?.outputKey || `Rule ${index + 1}`,
                      ruleIndex: index,
                      items: unwrappedItems,
                      itemCount: unwrappedItems.length,
                    };
                  },
                );
              } else {
                responseData = [
                  {
                    rule: rules[0]?.outputKey || "Output",
                    ruleIndex: 0,
                    items: rawOutputData,
                    itemCount: rawOutputData.length,
                  },
                ];
              }
            } else {
              responseData = rules.map((rule, index) => ({
                rule: rule.outputKey || `Rule ${index + 1}`,
                ruleIndex: index,
                items: [],
                itemCount: 0,
              }));
            }

            setOutputData(responseData);

            const totalCount =
              Array.isArray(responseData) && responseData[0]?.items
                ? responseData.reduce(
                    (sum: number, rule: any) => sum + (rule.itemCount || 0),
                    0,
                  )
                : Array.isArray(responseData)
                  ? responseData.length
                  : 0;

            if (data.onDataChange) {
              data.onDataChange(id, {
                response: responseData,
                outputData: responseData,
                itemCount: totalCount,
                node_data: nodeData,
              });
            }
          }
        }

        // Show appropriate toast based on execution result
        if (hasErrors) {
          const errorDescription = errorMessage
            ? `${errorNodeTitle}: ${errorMessage}`
            : "One or more nodes failed during execution";

          const shortError =
            errorDescription.length > 150
              ? errorDescription.substring(0, 150) + "..."
              : errorDescription;

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description: shortError,
            duration: 7000,
          });
        } else {
          toast({
            variant: "success",
            title: "Success",
            description: `Switch executed successfully`,
            duration: 3000,
          });
        }
      } else {
        // Fallback - no node results in response
        const isSuccess =
          result?.success === true ||
          result?.status === "success" ||
          result?.body?.status === 1 ||
          result?.body?.status === "1" ||
          result?.status === 1;

        if (isSuccess) {
          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "success",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "success",
            title: "Success",
            description: result?.message || "Switch executed successfully",
            duration: 3000,
          });
        } else {
          const allEdges = reactFlowInstance.getEdges();
          const downstreamNodes = findDownstreamNodes(id, allEdges);

          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error:
                      result?.body?.message ||
                      result?.message ||
                      "Execution failed",
                  },
                };
              } else if (downstreamNodes.has(node.id)) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error: "Not executed due to upstream failure",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description:
              result?.body?.message ||
              result?.message ||
              "Switch execution failed",
            duration: 4000,
          });
        }
      }
    } catch (error: any) {
      console.error("Switch Execution Error:", error);
      setErrorResponse(error?.message || "Switch execution failed");
      setOutputData(null);

      const allEdges = reactFlowInstance.getEdges();
      const downstreamNodes = findDownstreamNodes(id, allEdges);

      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: error?.message || "Switch execution failed",
              },
            };
          } else if (downstreamNodes.has(node.id)) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: "Not executed due to upstream failure",
              },
            };
          }
          return node;
        }),
      );

      toast({
        variant: "destructive",
        title: "Error",
        description: error?.message || "Switch execution failed",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Copy output to clipboard
  const handleCopyOutput = () => {
    const dataToCopy = errorResponse || outputData;
    if (!dataToCopy) return;
    navigator.clipboard.writeText(JSON.stringify(dataToCopy, null, 2));
    toast({
      variant: "success",
      title: "Copied!",
      description: "Output copied to clipboard.",
      duration: 2000,
    });
  };

  // Validation for rules and conditions with error messages
  const validateRulesAndConditions = (): {
    isValid: boolean;
    errorMessage?: string;
  } => {
    // Must have at least one rule
    if (!rules || rules.length === 0) {
      return {
        isValid: false,
        errorMessage: "Add at least one rule to evaluate",
      };
    }

    // Check for rule name errors (duplicates or length violations)
    if (ruleNameErrors.size > 0) {
      return {
        isValid: false,
        errorMessage: "Fix rule name errors before executing",
      };
    }

    // Check for empty rule names
    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i];
      if (!rule.outputKey || !rule.outputKey.trim()) {
        return {
          isValid: false,
          errorMessage: `Rule ${i + 1}: Rule name is required`,
        };
      }
    }

    // Check each rule
    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i];

      // Each rule must have at least one condition
      if (
        !rule.conditions.conditions ||
        rule.conditions.conditions.length === 0
      ) {
        return {
          isValid: false,
          errorMessage: `Rule ${i + 1} is empty. Add at least one condition to this rule.`,
        };
      }

      // Check each condition in the rule
      for (let j = 0; j < rule.conditions.conditions.length; j++) {
        const condition = rule.conditions.conditions[j];

        // Check if leftValue is filled
        if (!condition.leftValue?.trim()) {
          return {
            isValid: false,
            errorMessage: `Rule ${i + 1}, Condition ${j + 1}: Field (left value) is required`,
          };
        }

        // Check if operator type is selected
        if (!condition.operator.type) {
          return {
            isValid: false,
            errorMessage: `Rule ${i + 1}, Condition ${j + 1}: Operator type is required`,
          };
        }

        // Check if operator operation is selected
        if (!condition.operator.operation) {
          return {
            isValid: false,
            errorMessage: `Rule ${i + 1}, Condition ${j + 1}: Operator is required`,
          };
        }
      }
    }

    return { isValid: true };
  };

  // Render input data section with tabs for multiple sources
  const renderInputData = () => {
    if (!inputData?.length) {
      return (
        <div className="text-xs text-muted-foreground p-4 border rounded-md bg-muted/20 text-center">
          <div className="font-medium">No Input Connected</div>
          <div>Connect a node to see data here</div>
        </div>
      );
    }

    // Build sources array for PaginatedInputViewer (handles tabs internally)
    const sources = inputData.map((input, index) => {
      // Generate label: if from switch node, use "Rule X", otherwise use original label
      let displayLabel = input.label || `Source ${index + 1}`;

      // Check if label indicates switch node (e.g., "switch", "SWITCH")
      if (displayLabel.toLowerCase().includes("switch")) {
        displayLabel = `Rule ${index + 1}`;
      }

      return {
        label: displayLabel,
        data: getDisplayData(input),
        isConfig: !!input.configData, // Mark as config if source is configuration
      };
    });

    return (
      <PaginatedInputViewer
        sources={sources}
        onRefresh={refreshInputData}
        sourceCount={inputData.length}
      />
    );
  };

  // Helper function to get icon for data type
  const getDataTypeIcon = (dataType: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      number: <Hash className="h-3.5 w-3.5 mr-2" />,
      string: <Type className="h-3.5 w-3.5 mr-2" />,
      boolean: <ToggleLeft className="h-3.5 w-3.5 mr-2" />,
      date: <Calendar className="h-3.5 w-3.5 mr-2" />,
      object: <Braces className="h-3.5 w-3.5 mr-2" />,
      array: <List className="h-3.5 w-3.5 mr-2" />,
      dateTime: <Clock className="h-3.5 w-3.5 mr-2" />,
    };

    return iconMap[dataType] || <Type className="h-3.5 w-3.5 mr-2" />;
  };

  return (
    <FullscreenWrapper
      id={id}
      isExpanded={isExpanded}
      executionStatus={(data as any).executionStatus}
      borderColor={data.borderColor}
      shadowColor={data.shadowColor}
      onContextMenu={handleContextMenu}
      collapsedStyle={{
        height: isExpanded
          ? "auto"
          : rules.length > 2
            ? `${collapsedHeight}px`
            : undefined,
      }}
      expandedClassName="w-[90vw] !max-w-[1000px] flex flex-col"
    >
      <Confirmation
        text="Are you sure you want to delete this node?"
        confirmAction={confirmDelete}
        rejectAction={() => setShowDeleteConfirm(false)}
        confirmText="Confirm"
        rejectText="Cancel"
        show={showDeleteConfirm}
        closeDialog={() => setShowDeleteConfirm(false)}
      />

      {/* Context Menu */}
      <NodeContextMenu
        show={showContextMenu}
        position={contextMenuPosition}
        onClose={closeContextMenu}
        items={[
          createContextMenuItems.expandCollapse(isExpanded, () =>
            setIsExpanded((prev) => !prev),
          ),
          createContextMenuItems.delete(
            () => handleDelete(id),
            !!data.onDelete,
          ),
        ]}
      />

      {/* Handles */}
      <Handle
        id="input"
        type="target"
        position={Position.Left}
        className={handlerStyle}
      />

      {/* Dynamic output handles - based on node_data */}
      {/* node_data is synced with rules (placeholder before execution, actual data after) */}
      {(() => {
        const apiNodeData = (data as any).node_data || [];

        // Fallback: If no node_data exists (edge case), render based on rules
        if (apiNodeData.length === 0) {
          // If we have rules but no node_data yet, render handles for each rule
          if (rules.length > 0) {
            return rules.map((rule, index) => {
              const totalHandles = rules.length;
              const position = ((index + 1) / (totalHandles + 1)) * 100;
              const handleId = `output_${index}`;

              return (
                <div key={handleId}>
                  <Handle
                    id={handleId}
                    type="source"
                    position={Position.Right}
                    style={{ top: `${position}%` }}
                    className={handlerStyle}
                    title={`Output: ${rule.outputKey || `Rule ${index + 1}`}`}
                  />
                  {/* Handle label - visible when collapsed */}
                  {!isExpanded && (
                    <div
                      className="absolute text-[9px] font-semibold text-muted-foreground whitespace-nowrap pointer-events-none"
                      style={{
                        top: `${position}%`,
                        transform: "translateY(-50%)",
                        right: "-40px",
                      }}
                    >
                      {rule.outputKey || `R${index + 1}`} (0)
                    </div>
                  )}
                </div>
              );
            });
          }

          // No rules and no node_data - render default handle
          return (
            <div key="output_0">
              <Handle
                id="output_0"
                type="source"
                position={Position.Right}
                style={{ top: "50%" }}
                className={handlerStyle}
              />
            </div>
          );
        }

        return apiNodeData.map((item: any, index: number) => {
          const totalHandles = apiNodeData.length;
          const position = ((index + 1) / (totalHandles + 1)) * 100;
          const handleId = `output_${index}`;
          const outputLabel =
            item.outputKey || item.label || `Output ${index + 1}`;

          return (
            <div key={handleId}>
              <Handle
                id={handleId}
                type="source"
                position={Position.Right}
                style={{ top: `${position}%` }}
                className={handlerStyle}
                title={`Output: ${outputLabel}`}
              />
              {/* Handle label - visible when collapsed */}
              {!isExpanded && (
                <div
                  className="absolute bg-background text-primaryGreen rounded-xl px-1   text-[8px] font-semibold text-muted-foreground whitespace-nowrap pointer-events-none"
                  style={{
                    top: `${position}%`,
                    transform: "translateY(-50%)",
                    left: "calc(100% + 8px)", // ← always starts after the handle, grows right
                  }}
                >
                  {item.outputKey || item.label || `O${index + 1}`}
                </div>
              )}
            </div>
          );
        });
      })()}

      {/* Header */}
      <div
        className={`flex items-center justify-between gap-4 rounded-md transition-all duration-500 flex-shrink-0 ${
          isExpanded ? "p-3" : "!p-3"
        }`}
      >
        <CollapsedNodeView
          icon={<Signpost />}
          label={label}
          title={title}
          iconSize={35}
          borderColor={data.borderColor}
          onToggle={() => setIsExpanded((prev) => !prev)}
          isExpanded={isExpanded}
          executionStatus={(data as any).executionStatus}
          description={description}
          currentHeight={collapsedHeight}
        />
        <FullscreenToggleButton isExpanded={isExpanded} />
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Metadata Form */}
          <div className="mt-2 p-4 border-t flex items-start gap-4 flex-shrink-0">
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Title <span className="text-primary">*</span>
              </label>
              <ValidatedTitleInput
                id={id}
                title={title}
                onChange={setTitle}
                nodes={nodes}
                dataTitle={data.title}
                onValidationChange={setIsTitleValid}
              />
            </div>
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter node description"
                className="w-full p-2 border rounded text-xs resize-none text-muted-foreground bg-background"
                rows={2}
              />
            </div>
          </div>

          {/* 3 Section Grid */}
          <div
            ref={containerRef}
            className="flex gap-0 p-4 border-t flex-1 min-h-0 overflow-hidden"
          >
            {/* Left Section - Input Data (4/12 columns) */}
            <div
              style={{ width: `${widths[0]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection scrollable={true} hasRelative={true}>
                {renderInputData()}
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(0)} />

            {/* Middle Section - Config (4/12 columns) */}
            <div
              style={{ width: `${widths[1]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection hasRelative={true} scrollable={false}>
                {/* Single scroll surface — wraps all scrollable content */}
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-2 relative">
                  <h3 className="text-sm font-semibold sticky top-0 bg-background z-10 pb-2 border-b">
                    Parameters
                  </h3>

                  {/* Condition Builder */}
                  {operatorsLoading ? (
                    <div className="p-8 text-center border rounded-md bg-muted/10">
                      <div className="text-sm text-muted-foreground">
                        Loading operators...
                      </div>
                    </div>
                  ) : operatorsError ? (
                    <div className="p-8 text-center border rounded-md bg-muted/10">
                      <div className="text-sm text-destructive">
                        Failed to load operators
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Rules List */}
                      <div className="space-y-4">
                        {rules.map((rule, ruleIndex) => (
                          <div
                            key={rule.id}
                            className={`border-2 rounded-lg p-3 ${
                              ruleIndex % 2 === 0 ? "bg-muted" : "bg-border"
                            }`}
                          >
                            {/* Rule Header */}
                            <div className="flex items-center justify-between mb-3">
                              <label className="text-sm font-bold">
                                Rule {ruleIndex + 1}
                              </label>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeRule(rule.id)}
                                className="h-7 w-7 p-0 hover:bg-destructive/10"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>

                            <div className="p-3 space-y-3">
                              {/* Rule Output Configuration */}
                              <div
                                className="space-y-2 p-2.5 rounded-md border bg-background"
                                style={{
                                  borderLeftWidth: "3px",
                                  borderLeftColor: "var(--chart-3)",
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  <Workflow className="h-3.5 w-3.5 " />
                                  <label className="text-[11px] font-semibold  tracking-wide">
                                    Rule Configuration{" "}
                                    <span className="text-destructive">*</span>
                                  </label>
                                </div>

                                <div className="space-y-1">
                                  <Input
                                    value={rule.outputKey}
                                    onChange={(e) =>
                                      updateRule(
                                        rule.id,
                                        "outputKey",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="e.g., Rule Field Name (required)"
                                    className={`text-xs h-7 ${
                                      ruleNameErrors.has(rule.id)
                                        ? " focus-visible:ring-destructive"
                                        : ""
                                    }`}
                                    maxLength={35}
                                    required
                                  />
                                  {ruleNameErrors.has(rule.id) && (
                                    <p className="text-[10px] text-destructive mt-1">
                                      {ruleNameErrors.get(rule.id)}
                                    </p>
                                  )}
                                  <p className="text-[10px] text-muted-foreground mt-1 ml-auto w-fit">
                                    {rule.outputKey.length}/35 characters
                                  </p>
                                </div>

                                <div className="flex items-center gap-2 pt-1">
                                  <input
                                    type="checkbox"
                                    id={`rename-${rule.id}`}
                                    checked={rule.renameOutput}
                                    onChange={(e) =>
                                      updateRule(
                                        rule.id,
                                        "renameOutput",
                                        e.target.checked,
                                      )
                                    }
                                    className="h-3.5 w-3.5 rounded"
                                  />
                                  <label
                                    htmlFor={`rename-${rule.id}`}
                                    className="text-xs text-muted-foreground cursor-pointer"
                                  >
                                    Rename Output
                                  </label>
                                </div>
                              </div>

                              {/* Conditions for this rule */}
                              <div className="space-y-2">
                                {rule.conditions.conditions.map(
                                  (condition, condIndex) => (
                                    <React.Fragment key={condition.id}>
                                      <div className="border rounded-md p-2 bg-background space-y-2 border-l-[var(--chart-3)] border-l-2">
                                        {/* Condition Header */}
                                        <div className="flex items-center justify-between">
                                          <label className="text-xs font-semibold">
                                            Condition {condIndex + 1}
                                          </label>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() =>
                                              removeCondition(
                                                rule.id,
                                                condition.id,
                                              )
                                            }
                                            className="h-6 w-6 p-0 hover:bg-destructive/10"
                                          >
                                            <Trash2 className="h-3 w-3 text-destructive" />
                                          </Button>
                                        </div>

                                        {/* Left Value Input */}
                                        <div className="space-y-1">
                                          <label className="text-xs text-muted-foreground">
                                            Left Value
                                          </label>
                                          <ExpressionEditor
                                            value={condition.leftValue}
                                            onChange={(value) =>
                                              updateCondition(
                                                rule.id,
                                                condition.id,
                                                "leftValue",
                                                value,
                                              )
                                            }
                                            placeholder="Type {{$ for options or drag fields"
                                            availableFields={getAvailableFields}
                                            nodes={nodes}
                                            currentNodeId={id}
                                            minHeight="32px"
                                            maxHeight="120px"
                                          />
                                        </div>

                                        {/* Operator Dropdown */}
                                        <div className="space-y-1">
                                          <label className="text-xs text-muted-foreground">
                                            Operator
                                          </label>
                                          <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                              <Button
                                                variant="outline"
                                                className="w-full h-8 justify-between text-xs font-normal"
                                              >
                                                <span className="truncate">
                                                  {condition.operator.operation
                                                    ? `${condition.operator.type} - ${condition.operator.operation}`
                                                    : "Select operator"}
                                                </span>
                                                <ChevronRight className="h-3 w-3 ml-2 shrink-0" />
                                              </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent className="w-56">
                                              {operatorsData?.data?.map(
                                                (dataType: any) => {
                                                  const isSelectedType =
                                                    condition.operator.type ===
                                                    dataType.data_type;
                                                  return (
                                                    <DropdownMenuSub
                                                      key={dataType.data_type}
                                                    >
                                                      <DropdownMenuSubTrigger
                                                        className={`text-xs ${isSelectedType ? "text-primary font-medium" : ""}`}
                                                      >
                                                        <div className="flex items-center">
                                                          {getDataTypeIcon(
                                                            dataType.data_type,
                                                          )}
                                                          <span>
                                                            {
                                                              dataType.display_name
                                                            }
                                                          </span>
                                                        </div>
                                                      </DropdownMenuSubTrigger>
                                                      <DropdownMenuPortal>
                                                        <DropdownMenuSubContent>
                                                          {dataType.operator?.map(
                                                            (op: any) => {
                                                              const isSelected =
                                                                condition
                                                                  .operator
                                                                  .operation ===
                                                                  op.value &&
                                                                isSelectedType;
                                                              return (
                                                                <DropdownMenuItem
                                                                  key={op.value}
                                                                  className={`text-xs ${isSelected ? "text-primary font-medium bg-primary/10" : ""}`}
                                                                  onClick={() =>
                                                                    updateConditionOperator(
                                                                      rule.id,
                                                                      condition.id,
                                                                      dataType.data_type,
                                                                      op.value,
                                                                    )
                                                                  }
                                                                >
                                                                  {
                                                                    op.display_name
                                                                  }
                                                                </DropdownMenuItem>
                                                              );
                                                            },
                                                          )}
                                                        </DropdownMenuSubContent>
                                                      </DropdownMenuPortal>
                                                    </DropdownMenuSub>
                                                  );
                                                },
                                              )}
                                            </DropdownMenuContent>
                                          </DropdownMenu>
                                        </div>

                                        {/* Right Value - Always visible */}
                                        {(() => {
                                          // Find the selected operator from API data
                                          const selectedDataType =
                                            operatorsData?.data?.find(
                                              (dt: any) =>
                                                dt.data_type ===
                                                condition.operator.type,
                                            );
                                          const selectedOperator =
                                            selectedDataType?.operator?.find(
                                              (op: any) =>
                                                op.value ===
                                                condition.operator.operation,
                                            );

                                          // Determine if input is required (editable) or not (disabled)
                                          const requiresInput =
                                            selectedOperator?.requires_input !==
                                            false;

                                          return (
                                            <div className="space-y-1">
                                              <Input
                                                value={
                                                  condition.rightValue || ""
                                                }
                                                onChange={(e) =>
                                                  updateCondition(
                                                    rule.id,
                                                    condition.id,
                                                    "rightValue",
                                                    e.target.value,
                                                  )
                                                }
                                                disabled={!requiresInput}
                                                placeholder="Fields Value "
                                                className={`text-xs h-7 font-mono ${
                                                  !requiresInput
                                                    ? "bg-muted/30 cursor-not-allowed opacity-60"
                                                    : ""
                                                }`}
                                              />
                                            </div>
                                          );
                                        })()}
                                      </div>

                                      {/* Combinator between conditions - only show if not the last condition */}
                                      {condIndex <
                                        rule.conditions.conditions.length -
                                          1 && (
                                        <div className="flex items-center justify-center py-1">
                                          <Select
                                            value={rule.conditions.combinator}
                                            onValueChange={(
                                              value: "and" | "or",
                                            ) =>
                                              setRules((prev) =>
                                                prev.map((r) =>
                                                  r.id === rule.id
                                                    ? {
                                                        ...r,
                                                        conditions: {
                                                          ...r.conditions,
                                                          combinator: value,
                                                        },
                                                      }
                                                    : r,
                                                ),
                                              )
                                            }
                                          >
                                            <SelectTrigger className="w-20 h-6 text-xs font-semibold">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectGroup>
                                                <SelectItem
                                                  value="and"
                                                  className="text-xs"
                                                >
                                                  AND
                                                </SelectItem>
                                                <SelectItem
                                                  value="or"
                                                  className="text-xs"
                                                >
                                                  OR
                                                </SelectItem>
                                              </SelectGroup>
                                            </SelectContent>
                                          </Select>
                                        </div>
                                      )}
                                    </React.Fragment>
                                  ),
                                )}
                              </div>

                              {/* Add Condition Button for this rule */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => addCondition(rule.id)}
                                className="w-full border border-2 hover:border-solid"
                                style={{
                                  borderColor:
                                    "color-mix(in srgb, var(--chart-3) 50%, transparent)",
                                  color: "var(--chart-3)",
                                }}
                              >
                                <Plus className="h-3 w-3 mr-2" />
                                Add Condition
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Add Rule Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={addRule}
                        className="w-full border border-2 hover:border-solid"
                        style={{
                          borderColor:
                            "color-mix(in srgb, var(--chart-3) 50%, transparent)",
                          color: "var(--chart-3)",
                        }}
                      >
                        <Plus className="h-3 w-3 mr-2" />
                        Add Rule
                      </Button>
                    </div>
                  )}
                </div>
                {/* Execute Button */}
                <div className="sticky -bottom-1 left-0 right-0 bg-background border-t">
                  <Button
                    onClick={executeSwitch}
                    disabled={
                      !inputData ||
                      inputData.length === 0 ||
                      !title.trim() ||
                      !validateRulesAndConditions().isValid ||
                      loading ||
                      !isTitleValid
                    }
                    className="w-full mt-4 sticky bottom-0 left-0 right-0 rounded-b-md"
                    size="sm"
                  >
                    <FlaskConical size={14} className="mr-2" />
                    Execute step
                  </Button>
                </div>
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(1)} />

            {/* Right Section - Response (4/12 columns) */}
            <div style={{ flex: 1, minWidth: 80 }}>
              <NodeContentSection scrollable={false} hasRelative={true}>
                <ResponseViewer
                  response={errorResponse || outputData}
                  loading={loading}
                  emptyMessage="Execute SWITCH to see output preview"
                  loadingMessage="Processing..."
                  onCopy={handleCopyOutput}
                  isError={!!errorResponse}
                  isLoopChild={(data as any).is_loop_child || false}
                  totalIterations={(data as any).total_iterations || 0}
                  iterations={(data as any).iterations || []}
                  fillParent={true}
                  onResponseChange={(newData) => {
                    setOutputData(newData);
                    data.onDataChange?.(id, {
                      response: newData,
                      manuallyEdited: true,
                    });
                  }}
                />
              </NodeContentSection>
            </div>
          </div>
        </div>
      )}
    </FullscreenWrapper>
  );
};

export default SwitchNode;
